import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { ArrowLeft, Download, Clock, CheckCircle, XCircle, Loader2, FileText, FileImage, File as FileIcon, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import FileTree from '../components/FileTree';
import { toast } from 'sonner';

const API = '/api';

function formatSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function StatusBadge({ status }) {
  const map = {
    pending: { cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: Clock, label: 'Pending' },
    processing: { cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2, label: 'Processing' },
    completed: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle, label: 'Completed' },
    failed: { cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle, label: 'Failed' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`gap-1 text-xs font-medium border ${s.cls}`}>
      <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {s.label}
    </Badge>
  );
}

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);

  const fetchJob = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}`, { withCredentials: true });
      setJob(data);
    } catch {
      toast.error('Failed to load job');
      navigate('/');
    }
  }, [jobId, navigate]);

  useEffect(() => {
    fetchJob();
    const iv = setInterval(() => {
      fetchJob();
    }, 3000);
    return () => clearInterval(iv);
  }, [fetchJob]);

  useEffect(() => {
    if (job && (job.status === 'completed' || job.status === 'failed')) {
      // Stop polling when done
    }
  }, [job]);

  const handleFileSelect = async (file) => {
    if (file.type === 'folder') return;
    setSelectedFile(file);
    setLoadingFile(true);
    setFileContent(null);
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}/file`, {
        params: { path: file.path },
        withCredentials: true,
      });
      setFileContent(data);
    } catch {
      setFileContent({ type: 'error', content: 'Failed to load file' });
    } finally {
      setLoadingFile(false);
    }
  };

  const handleDownload = () => {
    const token = localStorage.getItem('access_token');
    const url = `${API}/jobs/${jobId}/download?token=${encodeURIComponent(token || '')}`;
    window.open(url, '_blank');
  };

  if (!job) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      </div>
    );
  }

  const renderPreview = () => {
    if (!selectedFile) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-zinc-400 p-8">
          <FileIcon className="w-10 h-10 mb-3 opacity-50" />
          <p className="text-sm font-body">Select a file from the tree to preview</p>
        </div>
      );
    }

    if (loadingFile) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
        </div>
      );
    }

    if (!fileContent || fileContent.type === 'error') {
      return (
        <div className="p-4 text-sm text-rose-600 font-body">
          {fileContent?.content || 'Failed to load file'}
        </div>
      );
    }

    if (fileContent.type === 'text') {
      return (
        <div className="p-0">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-200 bg-zinc-50">
            <FileText className="w-4 h-4 text-zinc-500" />
            <span className="text-xs font-mono text-zinc-600 truncate">{selectedFile.path}</span>
            <span className="ml-auto text-xs text-zinc-400">{formatSize(fileContent.size)}</span>
          </div>
          <ScrollArea className="h-[calc(100vh-320px)]">
            <pre className="p-4 text-xs font-mono text-zinc-700 whitespace-pre-wrap break-all leading-relaxed">
              {fileContent.content}
            </pre>
          </ScrollArea>
        </div>
      );
    }

    if (fileContent.type === 'image') {
      return (
        <div className="p-0">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-200 bg-zinc-50">
            <FileImage className="w-4 h-4 text-zinc-500" />
            <span className="text-xs font-mono text-zinc-600 truncate">{selectedFile.path}</span>
            <span className="ml-auto text-xs text-zinc-400">{formatSize(fileContent.size)}</span>
          </div>
          <div className="flex items-center justify-center p-8 bg-[repeating-conic-gradient(#f4f4f5_0%_25%,transparent_0%_50%)_0_0/20px_20px]">
            <img
              src={`${API}/jobs/${jobId}/file/raw?path=${encodeURIComponent(selectedFile.path)}&token=${localStorage.getItem('access_token') || ''}`}
              alt={selectedFile.name}
              className="max-w-full max-h-[60vh] object-contain"
              data-testid="image-preview"
            />
          </div>
        </div>
      );
    }

    return (
      <div className="p-4 flex flex-col items-center gap-2">
        <FileIcon className="w-8 h-8 text-zinc-400" />
        <p className="text-sm text-zinc-600 font-body">{fileContent.name}</p>
        <p className="text-xs text-zinc-400">{fileContent.content_type} - {formatSize(fileContent.size)}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="job-detail-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="mr-auto text-zinc-500 hover:text-zinc-900"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <StatusBadge status={job.status} />
          {job.status === 'completed' && (
            <Button
              onClick={handleDownload}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-5 h-9"
              data-testid="download-zip-button"
            >
              <Download className="w-4 h-4 mr-2" /> Download ZIP ({formatSize(job.zip_size)})
            </Button>
          )}
        </div>

        {/* Job Info */}
        <div className="border border-zinc-200 bg-white p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Original URL</span>
              <a
                href={job.original_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs text-blue-600 hover:underline flex items-center gap-1 truncate"
                data-testid="original-url-link"
              >
                {job.original_url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            </div>
            <div>
              <span className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Snapshot Date</span>
              <span className="text-zinc-700 text-xs font-mono">{job.timestamp}</span>
            </div>
            <div>
              <span className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Files</span>
              <span className="text-zinc-700 text-sm">{job.total_files || '-'}</span>
            </div>
            <div>
              <span className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Created</span>
              <span className="text-zinc-700 text-xs">
                {job.created_at ? new Date(job.created_at).toLocaleString() : '-'}
              </span>
            </div>
          </div>
          {job.status === 'processing' && (
            <div className="mt-4 flex items-center gap-3">
              <Progress value={job.progress} className="h-2 flex-1" />
              <span className="text-sm text-zinc-500 font-mono">{job.progress}%</span>
            </div>
          )}
          {job.status === 'failed' && job.error && (
            <div className="mt-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-sm" data-testid="job-error">
              {job.error}
            </div>
          )}
        </div>

        {/* File Tree + Preview */}
        {job.status === 'completed' && job.file_tree && job.file_tree.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-zinc-200 bg-white" data-testid="file-explorer">
            <div className="lg:col-span-1 border-r border-zinc-200">
              <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
                <span className="text-xs font-bold tracking-wider uppercase text-zinc-500">File Tree</span>
              </div>
              <ScrollArea className="h-[calc(100vh-380px)]">
                <FileTree
                  tree={job.file_tree}
                  onSelect={handleFileSelect}
                  selectedPath={selectedFile?.path}
                />
              </ScrollArea>
            </div>
            <div className="lg:col-span-2">
              <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
                <span className="text-xs font-bold tracking-wider uppercase text-zinc-500">Preview</span>
              </div>
              {renderPreview()}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
